// kevinz.ai — top-level app + tweaks wiring

import React from 'react';
import ReactDOM from 'react-dom';
import { useTweaks, TweaksPanel, TweakSection, TweakRadio, TweakToggle } from './tweaks-panel.jsx';
import { BootScreen } from './boot-screen.jsx';
import './ascii-portrait.jsx';
import {
  Hero, StatsBar, ProofSection, Commander, Writing, Credentials,
  Newsletter, WorkWithMe, Toolchain, Footer, AsciiRule, useKzMode
} from './components.jsx';
import { StickyNewsletter, CommanderDemo } from './enhancements.jsx';
import { FleetOps } from './fleet-ops.jsx';
import { AutomateExplainer } from './automate-explainer.jsx';
import { ClaudeFrontier } from './claude-frontier.jsx';
import { Topbar } from './topbar.jsx';
import {
  ProjectsTabbed, ReadyIQ, DMHub, MyWiFi, GuestNetworks, OpenClawHermes
} from './projects-deepdive.jsx';
import { AsciiPicCTA } from './ascii-cta.jsx';
import { Contact } from './contact.jsx';
import { GaryVFeatured } from './gary-v-modal.jsx';
import './analytics-events.jsx';

const KZ_TWEAK_DEFAULTS = /*EDITMODE-BEGIN*/{
  "theme": "auto",
  "displayFont": "mono",
  "scanlines": true,
  "showHero": true,
  "showStats": true,
  "showFleetOps": true,
  "showAutomate": true,
  "showFrontier": true,
  "showToolchain": true,
  "showNewsletter": true,
  "showCommander": true,
  "showProjects": true,
  "showWriting": true,
  "showXFeed": false,
  "showCredentials": true,
  "showConsulting": true,
  "showContact": true,
  "showAsciiCTA": true,
  "showGaryV": true
}/*EDITMODE-END*/;

function App() {
  const [tweaks, setTweak] = useTweaks(KZ_TWEAK_DEFAULTS);
  const [mode] = useKzMode();
  const isExec = mode === "exec";
  const showBuilderSections = !isExec;

  const [activeProject, setActiveProject] = React.useState(() => {
    const fromUrl = new URLSearchParams(window.location.search).get("company");
    return fromUrl || "readyiq";
  });
  // URL sync only on explicit tab click — no useEffect, no mount pollution
  const setActiveProjectWithSync = React.useCallback((val) => {
    setActiveProject(val);
    const url = new URL(window.location.href);
    if (val === "all" || val === "commander") {
      url.searchParams.delete("company");
    } else {
      url.searchParams.set("company", val);
    }
    window.history.replaceState(null, "", url.toString());
  }, []);

  React.useEffect(() => {
    document.body.dataset.scanlines = tweaks.scanlines ? "on" : "off";
    document.body.dataset.theme = tweaks.theme;
    document.body.dataset.font = tweaks.displayFont;
  }, [tweaks.scanlines, tweaks.theme, tweaks.displayFont]);

  // Standalone ASCII tool view: ?view=ascii renders only the AsciiPicCTA
  // with a slim back-nav. Shareable URL for Twitter/social traffic; same
  // React shell so styles/tokens are inherited (no duplicate CSS).
  const isAsciiView = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("view") === "ascii";

  React.useEffect(() => {
    if (isAsciiView) {
      document.title = "Your face in 80 characters — kevinz.ai";
    }
  }, [isAsciiView]);

  if (isAsciiView) {
    return (
      <div style={{ minHeight: "100vh", paddingTop: 0 }}>
        <div className="topbar">
          <div className="container topbar-inner" style={{ display: "flex", alignItems: "center", placeContent: "space-between", padding: "12px 24px" }}>
            <a href="/" style={{ display: "inline-flex", alignItems: "center", gap: 10, color: "inherit", textDecoration: "none" }}>
              <span style={{ width: 26, height: 26, border: "1px solid var(--amber)", color: "var(--amber)", display: "inline-flex", alignItems: "center", placeContent: "center", fontWeight: 700, fontSize: 12 }}>KZ</span>
              <span style={{ fontWeight: 700 }}>kevinz.ai</span>
              <span className="dim" style={{ marginLeft: 8, fontSize: 11 }}>/ ascii portrait tool</span>
            </a>
            <a href="/" style={{ fontSize: 11, color: "var(--fg-3)", letterSpacing: "0.16em", textTransform: "uppercase", textDecoration: "none" }}>← back</a>
          </div>
        </div>
        <AsciiPicCTA />
        <Footer />
      </div>
    );
  }

  return (
    <>
      <BootScreen />
      <Topbar />
      <AsciiRule />
      {/* CareerTimeline stays intentionally archived/unmounted; see enhancements.jsx status note. */}
      {tweaks.showHero && <Hero />}
      {/* NvidiaStrip merged inline into Hero partners-block for connected layout */}
      <AsciiRule />
      {showBuilderSections && tweaks.showStats && <StatsBar />}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showFleetOps && <FleetOps />}
      {showBuilderSections && tweaks.showFleetOps && <AsciiRule />}
      {showBuilderSections && tweaks.showAutomate && <AutomateExplainer />}
      {showBuilderSections && tweaks.showAutomate && <AsciiRule />}
      {showBuilderSections && tweaks.showFrontier && <ClaudeFrontier />}
      {showBuilderSections && tweaks.showFrontier && <AsciiRule />}
      {showBuilderSections && <ProofSection />}
      {showBuilderSections && <AsciiRule />}
      {tweaks.showNewsletter && <Newsletter />}
      <AsciiRule />
      {/* Stack tabs + deep-dive group renders in BOTH builder and exec modes — embedded full-width below the EXEC profile body */}
      {tweaks.showProjects && <ProjectsTabbed activeProject={activeProject} setActiveProject={setActiveProjectWithSync} />}
      {showBuilderSections && <AsciiRule />}
      {/* Deep-dive group: ReadyIQ → OpenClawHermes. No plain dividers between them.
          When activeProject==="all", each section prepends its own ASCII chapter marker.
          ReadyIQ renders first — it's the revenue product, Commander is free. */}
      {tweaks.showProjects && <ReadyIQ activeProject={activeProject} />}
      {tweaks.showCommander && <Commander activeProject={activeProject} />}
      {/* CommanderDemo is part of the Commander stack tab — only render when Commander is active or in all-stacked mode */}
      {(activeProject === "all" || activeProject === "commander") && <CommanderDemo />}
      {tweaks.showProjects && <DMHub activeProject={activeProject} />}
      {tweaks.showProjects && <MyWiFi activeProject={activeProject} />}
      {tweaks.showProjects && <GuestNetworks activeProject={activeProject} />}
      {tweaks.showProjects && <OpenClawHermes activeProject={activeProject} />}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showAsciiCTA && (
        <section data-section="ascii-cta" style={{ padding: "12px 0" }}>
          <div className="container" style={{ textAlign: "right" }}>
            <a
              href="/?view=ascii"
              style={{
                fontFamily: "var(--mono)",
                fontSize: 12,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
                textDecoration: "none",
              }}
            >$ make your own ASCII face →</a>
          </div>
        </section>
      )}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showWriting && <Writing />}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showGaryV && <GaryVFeatured />}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showCredentials && <Credentials />}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showConsulting && <WorkWithMe />}
      {showBuilderSections && <AsciiRule />}
      {showBuilderSections && tweaks.showToolchain && <Toolchain />}
      {showBuilderSections && <AsciiRule />}
      {tweaks.showContact && <Contact />}
      {/* X-feed rail removed per merge brief v4 §5 — file kept; tweaks toggle preserved for revival */}
      <Footer />
      <StickyNewsletter />

      <TweaksPanel title="Tweaks">
        <TweakSection title="Theme">
          <TweakRadio
            label="Accent"
            value={tweaks.theme}
            onChange={(v) => setTweak("theme", v)}
            options={[
              { value: "auto", label: "Auto · per-section (Vercel)" },
              { value: "blue", label: "Vercel Blue" },
              { value: "violet", label: "Violet" },
              { value: "green", label: "Green" },
              { value: "magenta", label: "Magenta" },
              { value: "cyan", label: "Cyan" },
              { value: "amber", label: "Amber" },
              { value: "white", label: "Mono / white" },
            ]}
          />
          <TweakRadio
            label="Headline font"
            value={tweaks.displayFont}
            onChange={(v) => setTweak("displayFont", v)}
            options={[
              { value: "mono", label: "JetBrains Mono (default)" },
              { value: "departure", label: "Departure Mono (pixel)" },
              { value: "grotesk", label: "Space Grotesk (modern)" },
              { value: "geist", label: "Geist (Vercel-ish)" },
              { value: "serif", label: "Instrument Serif (italic)" },
            ]}
          />
        </TweakSection>

        <TweakSection title="CRT">
          <TweakToggle label="Scanlines" value={tweaks.scanlines} onChange={(v) => setTweak("scanlines", v)} />
        </TweakSection>

        <TweakSection title="Sections">
          <TweakToggle label="Hero" value={tweaks.showHero} onChange={(v) => setTweak("showHero", v)} />
          <TweakToggle label="Stats bar" value={tweaks.showStats} onChange={(v) => setTweak("showStats", v)} />
          <TweakToggle label="Fleet ops board" value={tweaks.showFleetOps} onChange={(v) => setTweak("showFleetOps", v)} />
          <TweakToggle label="AI automation explainer" value={tweaks.showAutomate} onChange={(v) => setTweak("showAutomate", v)} />
          <TweakToggle label="Claude frontier showcase" value={tweaks.showFrontier} onChange={(v) => setTweak("showFrontier", v)} />
          <TweakToggle label="Tools I use" value={tweaks.showToolchain} onChange={(v) => setTweak("showToolchain", v)} />
          <TweakToggle label="Newsletter" value={tweaks.showNewsletter} onChange={(v) => setTweak("showNewsletter", v)} />
          <TweakToggle label="Commander" value={tweaks.showCommander} onChange={(v) => setTweak("showCommander", v)} />
          <TweakToggle label="Projects" value={tweaks.showProjects} onChange={(v) => setTweak("showProjects", v)} />
          <TweakToggle label="Writing" value={tweaks.showWriting} onChange={(v) => setTweak("showWriting", v)} />
          <TweakToggle label="X / Building in Public" value={tweaks.showXFeed} onChange={(v) => setTweak("showXFeed", v)} />
          <TweakToggle label="Credentials" value={tweaks.showCredentials} onChange={(v) => setTweak("showCredentials", v)} />
          <TweakToggle label="Consulting CTA" value={tweaks.showConsulting} onChange={(v) => setTweak("showConsulting", v)} />
          <TweakToggle label="Contact + Cal.com" value={tweaks.showContact} onChange={(v) => setTweak("showContact", v)} />
          <TweakToggle label="ASCII Profile CTA" value={tweaks.showAsciiCTA} onChange={(v) => setTweak("showAsciiCTA", v)} />
          <TweakToggle label="Gary V archive" value={tweaks.showGaryV} onChange={(v) => setTweak("showGaryV", v)} />
        </TweakSection>
      </TweaksPanel>
    </>
  );
}

ReactDOM.createRoot(document.getElementById("root")).render(<App />);
